import Chart from "chart.js/auto";
import Editor from "@toast-ui/editor";
import * as dom from "../lib/dom";
import {
  isTab,
  handleTabClick,
  handleTabKeyDown,
  isMenuButton,
  hideAllMenus,
  toggleMenu,
  isGroupSelectable,
  toggleGroupSelectable,
  isAccordionHeader,
  toggleAccordionPanel,
  showModal,
  isCarouselButton,
  handleCarouselButton,
  handleCarouselPanelMouseDown,
  handleCarouselPanelMouseUp,
  handleCarouselPanelMouseMove,
  handleCarouselPanelMouseLeave,
  handleCarouselPanelMouseOut,
} from "../lib/a11y";

document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("textarea[data-toast]").forEach((textarea) => {
    if (!(textarea instanceof HTMLTextAreaElement)) {
      return;
    }

    // Toast UI Editor 는 자체 <div> 컨테이너에 마운트. 원본 textarea 는
    // 시각적으로 숨기되 form submit 대상으로 유지 — change 마다 markdown 동기화.
    const host = document.createElement("div");
    host.classList.add("flex-auto");
    textarea.classList.add("sr-only");
    textarea.tabIndex = -1;
    textarea.setAttribute("aria-hidden", "true");
    textarea.parentNode?.insertBefore(host, textarea);

    const editor = new Editor({
      el: host,
      height: "400px",
      initialEditType: "wysiwyg",
      previewStyle: "vertical",
      initialValue: textarea.value,
      usageStatistics: false,
      // 기본 toolbar 에서 `code` (inline code) / `codeblock` 그룹 제거.
      // 공시 본문에 코드 입력은 사용 사례 없음.
      toolbarItems: [
        ["heading", "bold", "italic", "strike"],
        ["hr", "quote"],
        ["ul", "ol", "task", "indent", "outdent"],
        ["table", "image", "link"],
        ["scrollSync"],
      ],
    });

    const syncToTextarea = () => {
      textarea.value = editor.getMarkdown();
    };
    editor.on("change", syncToTextarea);

    // form submit 직전에도 한 번 동기화 — change 이벤트가 누락된 경우 대비.
    textarea.form?.addEventListener("submit", syncToTextarea);
  });

  document
    .querySelectorAll("form select[multiple][data-alternate-input-for]")
    .forEach((select) => {
      if (!(select instanceof HTMLSelectElement)) {
        return;
      }

      const inputId = select.dataset["alternateInputFor"];
      const input = document.getElementById(inputId as string);
      if (input instanceof HTMLInputElement) {
        input.value = [...select.selectedOptions]
          .map((option) => option.label)
          .join(", ");
        input.addEventListener("input", (event) => {
          const removeWhiteSpace = (v: string) => v.replaceAll(/\s/g, "");
          const availableLabels = new Set(
            [...select.options].map((option) => removeWhiteSpace(option.label)),
          );
          const optionsMap = new Map(
            [...select.options].map((option, index) => [
              removeWhiteSpace(option.label),
              index,
            ]),
          );

          const labelsFromInput = input.value
            .split(/,/)
            .map((label) => removeWhiteSpace(label.trim()));
          const labelsFromSelect = new Set(
            [...select.selectedOptions].map((option) =>
              removeWhiteSpace(option.label),
            ),
          );

          const labelsToAdd = (() => {
            const result = [];
            for (const label of labelsFromInput) {
              if (availableLabels.has(label) && !labelsFromSelect.has(label)) {
                result.push(label);
              }
            }
            return result;
          })();
          const labelsToRemove = (() => {
            const result = [];
            for (const label of labelsFromSelect.keys()) {
              if (!labelsFromInput.includes(label)) {
                result.push(label);
              }
            }
            return result;
          })();

          if (labelsToAdd.length > 0 || labelsToRemove.length > 0) {
            for (const label of labelsToAdd) {
              const index = optionsMap.get(label);
              if (typeof index !== "undefined") {
                select.options.item(index)!.selected = true;
              }
            }
            for (const label of labelsToRemove) {
              const index = optionsMap.get(label);
              if (typeof index !== "undefined") {
                select.options.item(index)!.selected = false;
              }
            }
          }
        });
        select.addEventListener("change", (event) => {
          const labelsFromInput = input.value
            .split(/,/)
            .map((label) => label.trim());
          const labelsFromSelect = new Set(
            [...select.selectedOptions].map((option) => option.label),
          );

          const labelsToAdd = (() => {
            const result = [];
            for (const label of labelsFromSelect.keys()) {
              if (!labelsFromInput.includes(label)) {
                result.push(label);
              }
            }
            return result;
          })();
          const labelsToRemove = (() => {
            const result = [];
            for (const label of labelsFromInput) {
              if (!labelsFromSelect.has(label)) {
                result.push(label);
              }
            }
            return result;
          })();

          if (labelsToAdd.length > 0 || labelsToRemove.length > 0) {
            for (const label of labelsToAdd) {
              labelsFromInput.push(label);
            }
            for (const label of labelsToRemove) {
              const index = labelsFromInput.indexOf(label);
              if (index !== -1) {
                labelsFromInput.splice(index, 1);
              }
            }
            input.value = labelsFromInput.join(", ");
          }
        });
      }
    });

  document.body.addEventListener("click", (event) => {
    const target = event.target;
    if (target instanceof Element) {
      const modalSource = target.closest("[data-modal]");
      if (modalSource instanceof HTMLElement) {
        const modalId = modalSource.dataset["modal"] ?? "";
        const modal = document.getElementById(modalId);
        if (modal instanceof HTMLDialogElement) {
          showModal(modal);
        }

        event.stopPropagation();
        event.preventDefault();
      }
    }

    if (handleClick(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("keydown", (event) => {
    if (handleKeyDown(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("mousedown", (event) => {
    if (handleMouseDown(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("mouseup", (event) => {
    if (handleMouseUp(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("mousemove", (event) => {
    if (handleMouseMove(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("mouseleave", (event) => {
    if (handleMouseLeave(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("mouseout", (event) => {
    if (handleMouseOut(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document.body.addEventListener("change", (event) => {
    if (handleChange(event)) {
      event.stopPropagation();
      event.preventDefault();
    }
  });

  document
    .querySelectorAll<HTMLDialogElement>("dialog[data-landing]")
    .forEach((dialog) => {
      const key = `${dialog.dataset.landing}--expires`;
      const expiresInUnixSeconds = ((key) => {
        const localValue = localStorage.getItem(key);
        if (typeof localValue === "string" && localValue.length > 0) {
          const expires = Number.parseInt(localValue, 10);
          if (Number.isSafeInteger(expires)) {
            return expires;
          }
        }

        return undefined;
      })(key);
      if (typeof expiresInUnixSeconds === "number") {
        if (Math.floor(Date.now() / 1000) < expiresInUnixSeconds) {
          return;
        } else {
          localStorage.removeItem(key);
        }
      }

      dialog
        .querySelectorAll(`form[method="dialog"] button[type="submit"]`)
        .forEach((button) => {
          button.addEventListener("click", (event) => {
            const target = event.target;
            if (!(target instanceof Element)) {
              return;
            }

            const submit = target.closest(`button[type="submit"]`);
            const form = target.closest<HTMLFormElement>(
              `form[method="dialog"]`,
            );
            const dialog = target.closest<HTMLDialogElement>("dialog");
            if (!submit || !form || !dialog) {
              return;
            }

            const hideForAWeek = form.elements.namedItem("hide-for-a-week");
            if (hideForAWeek instanceof HTMLInputElement) {
              dialog.close(hideForAWeek.checked ? "checked" : "");

              event.stopPropagation();
              event.preventDefault();
            }
          });
        });

      dialog.addEventListener("close", () => {
        const durationInMilliseconds = 7 * 24 * 60 * 60 * 1000;
        const expires = Math.floor(
          (Date.now() + durationInMilliseconds) / 1000,
        );
        if (dialog.returnValue === "checked") {
          localStorage.setItem(key, expires.toString(10));
        }
      });

      dialog.showModal();
    });

  function updateLabelInDetails(details: HTMLDetailsElement) {
    const elements = details.querySelectorAll("[data-label-on-open]");
    for (const element of elements) {
      if (
        !(element instanceof HTMLElement) &&
        !(element instanceof SVGElement)
      ) {
        continue;
      }

      if (details.open) {
        element.ariaLabel = element.dataset.labelOnOpen ?? "";
      } else {
        element.ariaLabel = element.dataset.labelOnClosed ?? "";
      }
    }
  }

  document.querySelectorAll("details").forEach((details) => {
    updateLabelInDetails(details);

    details.addEventListener("toggle", (event) => {
      const target = event.target;
      if (!(target instanceof Element)) {
        return;
      }

      const details = target.closest("details");
      if (!(details instanceof HTMLDetailsElement)) {
        return;
      }

      updateLabelInDetails(details);
    });
  });

  const mainMenuToggle = document.querySelector(".main-menu-toggle");
  if (mainMenuToggle instanceof HTMLButtonElement) {
    mainMenuToggle.addEventListener("click", function (event) {
      const toggle = this;
      const isExpanded = toggle.getAttribute("aria-expanded") === "true";
      if (isExpanded) {
        toggle.setAttribute("aria-expanded", "false");

        const header = toggle.closest("header");
        if (header) {
          header.classList.remove("show-main-menu");
        }
      } else {
        toggle.setAttribute("aria-expanded", "true");

        const header = toggle.closest("header");
        if (header) {
          header.classList.add("show-main-menu");
        }
      }
      event.stopPropagation();
      event.preventDefault();
    });
  }
});

document.addEventListener("DOMContentLoaded", () => {
  for (const chart of [...document.getElementsByClassName("line-chart")]) {
    if (!(chart instanceof HTMLCanvasElement)) {
      continue;
    }

    const table = document.getElementById(chart.dataset["for"]);
    if (!(table instanceof HTMLTableElement)) {
      continue;
    }

    const rows = [...table.rows].slice(1);
    const labels = rows.map((row) => row.cells[0].textContent);
    const values = rows.map((row) =>
      Number.parseFloat(row.cells[1].textContent),
    );

    const options = (() => {
      if (chart.classList.contains("simple") === false) {
        return {
          interaction: {
            mode: "index",
            intersect: false,
          },
          responsive: true,
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: {
                callback: function (val, index) {
                  // Hide every 2nd tick label
                  return index % 2 === 0 ? this.getLabelForValue(val) : "";
                },
              },
            },
            y: {
              grid: {
                display: false,
              },
            },
          },
        };
      } else {
        return {
          interaction: {
            mode: "index",
            intersect: false,
          },
          events: [],
          responsive: true,
          plugins: {
            legend: {
              display: false,
            },
          },
          scales: {
            x: {
              grid: {
                display: false,
              },
              ticks: false,
            },
            y: {
              display: false,
            },
          },
        };
      }
    })();

    new Chart(chart, {
      type: "line",
      data: {
        labels: labels,
        datasets: [
          {
            label: "수익률",
            data: values,
            borderWidth: 1,
            pointStyle: false,
            cubicInterpolationMode: "monotone",
            borderColor: "#FF9E1B",
          },
        ],
      },
      options: options,
    });
  }
});

function handleClick(event: MouseEvent) {
  for (const target of dom.ancestors(event.target)) {
    if (isTab(target)) {
      handleTabClick(target);
      return true;
    }

    if (isMenuButton(target)) {
      toggleMenu(target);
      return true;
    }

    if (isAccordionHeader(target)) {
      toggleAccordionPanel(target);
      return true;
    }

    if (isGroupSelectable(target)) {
      toggleGroupSelectable(target);
      return false;
    }

    if (isCarouselButton(target)) {
      handleCarouselButton(target);
      return true;
    }
  }

  return false;
}

function handleKeyDown(event: KeyboardEvent) {
  for (const target of dom.ancestors(event.target)) {
    if (isTab(target)) {
      return handleTabKeyDown(target, event.key);
    }
  }

  return false;
}

function handleMouseDown(event: MouseEvent) {
  if (document.body.hasAttribute("data-menu-expanded")) {
    for (const target of dom.ancestors(event.target)) {
      if (target.role === "menu" || isMenuButton(target)) {
        return false;
      }
    }

    hideAllMenus();
    return true;
  }

  if (handleCarouselPanelMouseDown(event)) {
    return true;
  }

  return false;
}

function handleMouseMove(event: MouseEvent) {
  if (handleCarouselPanelMouseMove(event)) {
    return true;
  }

  return false;
}

function handleMouseUp(event: MouseEvent) {
  if (handleCarouselPanelMouseUp(event)) {
    return true;
  }

  return false;
}

function handleMouseLeave(event: MouseEvent) {
  if (handleCarouselPanelMouseLeave(event)) {
    return true;
  }

  return false;
}

function handleMouseOut(event: MouseEvent) {
  if (handleCarouselPanelMouseOut(event)) {
    return true;
  }

  return false;
}

function handleChange(event: Event) {
  return false;
}
